import { parseISO } from 'date-fns';

import { CompanyAutoOptionAdapter } from '@app/shared/models/company';
import { JobLocation } from '@shared/models/job-location';
import { isTruthy, Trackable } from '@shared/utils';

import { Candidate, CandidateAdapter } from './candidate';
import { LocationInput } from './location-input';
import { TeamMember } from './team-members.model';

export enum JobTypes {
  FullTime = 'Full-time',
  PartTime = 'Part-time',
  Temporary = 'Temporary',
  SOW = 'SOW',
  Direct = 'Direct placement',
}

export enum TimePeriods {
  Annually = 'Annually',
  Monthly = 'Monthly',
  Weekly = 'Weekly',
  Daily = 'Daily',
  Hourly = 'Hourly',
}
export interface Job<T = TeamMember> extends Trackable {
  id: number;
  accountManagerIds: number[];
  accountManagerName: string;
  additionalInformation?: string;
  assignmentCount: string;
  attachmentsLoaded: boolean;
  billRateCurrency?: string;
  billRatePeriod?: string;
  bonus?: number;
  bonusCurrency?: string;
  bonusPeriod?: string;
  budget?: any;
  budgetHighNational: number;
  budgetHighSf: number;
  budgetLowNational: number;
  budgetLowSf: number;
  budgetPayscaleTarget: string;
  budgetRole: string;
  budgetTechnologyFunction: string;
  candidates?: CandidateAdapter[];
  clientReqId?: string;
  commission?: number;
  company?: any;
  companyContact?: any;
  companyDescription?: string;
  companyId: number;
  companyJobId?: any;
  companyManager?: any;
  companyManagerId: number;
  companyName: string;
  coordinators: T[];
  createdById?: any;
  createdByName?: any;
  createdDate: string;
  description: string;
  duration?: number;
  durationMonths?: number;
  durationYears?: number;
  education?: string;
  externalDisplayId: string;
  externalId: string;
  featured?: (CandidateAdapter | Candidate)[];
  highLevelRequirements?: any;
  hiringManagerIds: number[];
  hiringManagerName?: any;
  hiringManagers: T[];
  industry: string;
  irpJobId?: any;
  isfeatured: string;
  jobType: string;
  lastModifiedById: string;
  lastModifiedByName: string;
  lastModifiedDate: string;
  locationInput?: LocationInput[];
  locations: JobLocation[];
  maxBillRate?: number;
  maxPayRate?: number;
  maxSalary?: number;
  minBillRate?: number;
  minPayRate?: number;
  minSalary?: number;
  numberOfPositions: number;
  opportunityReferral?: any;
  payRateCurrency?: string;
  payRatePeriod?: string;
  percentile?: number;
  probability: number;
  published: boolean;
  publishedInternally?: boolean;
  publishedDate: string;
  qualification?: any;
  recruiterIds: number[];
  recruiters?: { name: string }[];
  recruitingManagers: T[];
  regularRecruiters: T[];
  remote: boolean;
  responsibilities?: any;
  salaryCurrency?: string;
  salaryPeriod?: string;
  status: string;
  stock?: number;
  targetDate?: any;
  title: string;
  workExperience?: string;
  yearsOfExperience?: number;
  departments: any[];
  jobFunction: string;
}

export interface AdaptedJob extends Job {
  /** targetDate parsed as Date object */
  date: Date;
}

/**
 * isSingleJobObject checks if provided object describes single job or if it's a page object
 */
export function isSingleJobObject({
  regularRecruiters,
  recruitingManagers,
  hiringManagers,
  coordinators,
}: Job) {
  return !!(regularRecruiters && recruitingManagers && hiringManagers && coordinators);
}

// TODO: refactor to use proper returned type
export const adaptJob = (job: Job): AdaptedJob =>
  !!job
    ? {
        ...job,
        date: !!job.targetDate
          ? typeof job.targetDate === 'string'
            ? parseISO(job.targetDate)
            : new Date(job.targetDate)
          : null,
        durationYears: Math.floor(job.duration / 12),
        durationMonths: job.duration % 12,
        salaryCurrency: job.salaryCurrency ? job.salaryCurrency : 'USD',
        salaryPeriod: job.salaryPeriod ? job.salaryPeriod : 'Annually',
        bonusPeriod: job.bonusPeriod ? job.bonusPeriod : 'Annually',
        bonusCurrency: job.bonusCurrency ? job.bonusCurrency : 'USD',
        locationInput: getLocationInput(job.locations),
        company: CompanyAutoOptionAdapter.toOption(job.company),
      }
    : null;

export const getStateCode = (stateCode: string) =>
  !!stateCode && stateCode.length >= 2 ? stateCode.substr(0, 2).toUpperCase() : '';

export const mapToLocationName = (
  city: string,
  isUS: boolean,
  stateCode: string,
  countryName: string,
  location: string,
) =>
  city
    ? [city, isUS ? getStateCode(stateCode) : '', countryName].filter(isTruthy).join(', ')
    : location;

// TODO: define returned type
const getLocationInput = (locations) =>
  locations
    ? locations.map((jobLocation: JobLocation) => ({
        id: jobLocation.id,
        name: jobLocation.city || jobLocation.location,
        stateCode: jobLocation.state,
        countryCode: jobLocation.country,
        countryName: jobLocation.countryName,
        label: mapToLocationName(
          jobLocation.city,
          jobLocation.country === 'US' || jobLocation.country === 'United States',
          jobLocation.state,
          jobLocation.countryName,
          jobLocation.location,
        ),
      }))
    : [];
